import React, {Component} from 'react';
import css from './DataFilter.module.scss';
import axios from "axios";
import ErrorLabel from "../../../components/ErrorLabel/ErrorLabel";
import _ from 'lodash';
import Alert from "../../../components/Alert/Alert";


/**
 * @class
 *
 * Data Filters section in Settings page.
 */
const DataFilters = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();

    FIELD_STAGES = 'stages';
    FIELD_TYPES = 'types';
    FIELD_STATUS = 'fin_stages';

    // ---------- state: ----------
    state = {
        activeFieldTab: this.FIELD_STAGES,
        isFormValid: true,
        stageFields: [],
        typeFields: [],
        statusFields: [],
        shouldShowAlert: false,
        alertMessage: ''
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        axios.get(
            '/sf/metaData',
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                this.setState({
                    stageFields:   this.extractData(res.data, this.FIELD_STAGES),
                    typeFields:    this.extractData(res.data, this.FIELD_TYPES),
                    statusFields:  this.extractData(res.data, this.FIELD_STATUS),
                });
            })
            .catch(err => {
                if (axios.isCancel(err)) {
                    console.log('Request canceled');
                } else {
                    console.log('(GET) Error:\n', err);
                }
            });
    }

    componentWillUnmount() {
        this.source.cancel();
    }

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf DataFilters
     *
     * Extracts and maps the metadata fields from the API response object.
     * Our response object has fields (e.g. `stages`, `types`, and `fin_stages`) -- which we want. In the response object, these individual fields are split into
     *  lists like `visible` and `required` fields -- we want to combine these separate lists into a abject with these properties instead. This helper method
     *  is used for that purpose.
     *
     * @param rawData {object}: The entire response data.
     * @param key {string}: The key in the response object (e.g. `stages`, `types`, or `fin_stages` which are properties of the response object).
     *
     * @return {array}: List of (mapped) metadata fields.
     */
    extractData = (rawData, key) => {
        const fields = [];
        const comm_trigger = _.map(rawData[key].comm_trigger, (field) => {
            return field.label;
        });

        _.each(rawData[key].visible, (field) => {
            field.visible = true;
            if (comm_trigger.indexOf(field.label) !== -1){
                field.comm_trigger = true;
            }
            fields.push(field);
        });

        _.each(rawData[key].hidden, (field) => {
            field.visible = false;
            field.required = false;
            fields.push(field);
        });

        if (!rawData[key].comm_percentage) {
            return fields;
        }

        _.each(fields, (field) => {
            field.comm_percentage = rawData[key].comm_percentage[field.label] ? rawData[key].comm_percentage[field.label] : 0;
        });

        return fields;
    };

    /**
     * @method
     * @methodOf DataFilters
     *
     * Process/maps data to correct data structure for POST request.
     * This is the opposite to `extractData` in that we have to split our data back into separate lists (e.g. `visible`, `hidden`, etc.).
     *
     * @return {object}: Payload object.
     */
    prepPayload = () => {
        const fields = this.displayedFields();
        let result = {
            visible:[],
            hidden:[],
            comm_trigger:[],
            comm_percentage:{}
        };

        _.each(fields, field => {
            const f = {
                value:field.value,
                label:field.label
            };

            result.comm_percentage[field.label] = field.comm_percentage;

            if (field.visible) {
                result.visible.push(f);
            } else {
                result.hidden.push(f);
            }

            if (field.comm_trigger) {
                result.comm_trigger.push(f);
            }
        });

        return {
            data: result
        };
    };

    /**
     * @method
     * @methodOf DataFilters
     *
     * The displayed fields -- selected by the active field tab.
     *
     * @return {array}: Fields.
     */
    displayedFields = () => {
        let fields = [];

        switch(this.state.activeFieldTab) {
            case this.FIELD_STAGES:
                fields = _.cloneDeep(this.state.stageFields);
                break;
            case this.FIELD_TYPES:
                fields = _.cloneDeep(this.state.typeFields);
                break;
            case this.FIELD_STATUS:
                fields = _.cloneDeep(this.state.statusFields);
                break;
            default:
                break;
        }

        return fields;
    };

    /**
     * @method
     * @methodOf DataFilters
     *
     * Sets the active field in the state.
     * Styles and displayed content on the page/view is predicated on the active field.
     *
     * Fields for this section include: "customer" and "lead".
     *
     * @param field {string}: Name of field to toggle visibility. Fields include: "stages", "types", "status".
     *
     * @returns {undefined}: Sets the `activeFieldTab` state.
     */
    handleSetActiveFieldTab = (field) => {
        this.setState({
            activeFieldTab: field
        });
    };

    /**
     * @method
     * @methodOf DataFilters
     *
     * Dynamically sets button CSS based on the currently active field.
     *
     * @param field {string}: Name of field.
     *
     * @returns {string}: generated CSS string.
     */
    activeFieldTabButtonCss = (field) => {
        return this.state.activeFieldTab === field ? 'btn btn-primary' : 'btn btn-light';
    };

    /**
     * @method
     * @methodOf DataFilters
     *
     * Handler for when user toggles checkbox -- updates fields state.
     *
     * @param updatedField {object}: The object from the list of objects (`displayedFields`).
     * @param checkboxType {string}: The property of the field we want to update (e.g. `comm_trigger`).
     */
    checkboxOnChange = (updatedField, checkboxType) => {
        let fields = this.displayedFields();

        fields = fields.map(field => {
            if (field.value === updatedField.value) {
                const updatedField = {...field};
                updatedField[checkboxType] = !field[checkboxType];
                return updatedField;
            }
            return field;
        });

        switch (this.state.activeFieldTab) {
            case this.FIELD_STAGES:
                this.setState({ stageFields: fields });
                break;
            case this.FIELD_TYPES:
                this.setState({ typeFields: fields });
                break;
            case this.FIELD_STATUS:
                this.setState({ statusFields: fields });
                break;
            default:
                break;
        }
    };

    /**
     * @method
     * @methodOf DataFilters
     *
     * @param updatedField {object}: The object from the list of objects (`displayedFields`).
     * @param inputType {string}: The property of the field we want to update (e.g. `comm_trigger`).
     * @param val: Value of the input tag.
     */
    inputOnChange = (updatedField, inputType, val) => {
        let fields = this.displayedFields();

        fields = fields.map(field => {
            if (field.value === updatedField.value) {
                const updatedField = {...field};
                updatedField[inputType] = val;
                return updatedField;
            }
            return field;
        });

        switch (this.state.activeFieldTab) {
            case this.FIELD_STAGES:
                this.setState({ stageFields: fields });
                break;
            case this.FIELD_TYPES:
                this.setState({ typeFields: fields });
                break;
            case this.FIELD_STATUS:
                this.setState({ statusFields: fields });
                break;
            default:
                break;
        }
    };

    /**
     * @method
     * @methodOf DataFilters
     *
     * Checks if form is valid.
     * Only the `stages` fields need validation. We check if the percentage values of the input add up to 100%.
     *
     * @return {boolean}: True if form is valid or false if not valid.
     */
    formIsValid = () => {
        // we only need to validate `stages` fields:
        if (this.state.activeFieldTab !== this.FIELD_STAGES) {
            return true;
        }

        let isValid;

        let sum = this.displayedFields()
            .reduce((previous, current) => {
                const currentPercent = current.comm_trigger ? parseInt(current.comm_percentage, 10) : 0;
                return previous + currentPercent;
            }, 0);

        // if percentages don't add up to 100, show error label:
        isValid = sum === 100;

        if (isValid) {
            this.setState({ isFormValid: true });
        } else {
            this.setState({ isFormValid: false });
        }

        return isValid;
    };

    /**
     * @method
     * @methodOf DataFilters
     *
     * Saves data via making a POST API request.
     */
    saveSchemeOnClick = () => {
        // check if form is valid:
        if (!this.formIsValid()) {
            return;
        }

        const payload = this.prepPayload();

        axios.post(
            `/settings/filters/${this.state.activeFieldTab}`,
            payload,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                this.setState({
                    shouldShowAlert: true,
                    alertMessage: 'Data Saved Successfully.'
                });
                setTimeout(() => {
                    this.setState({
                        shouldShowAlert: false,
                    });
                }, 5500);
            })
            .catch(err => console.warn(`post error: ${err}`));
    };

    render() {
        return (
            <div className={css.DataFilter}>
                <Alert showAlert={this.state.shouldShowAlert}>{this.state.alertMessage}</Alert>

                <div className="row">
                    <div className="col-12 col-lg-7">
                        <button
                            className={this.activeFieldTabButtonCss(this.FIELD_STAGES)}
                            type="button"
                            onClick={() => this.handleSetActiveFieldTab(this.FIELD_STAGES)}
                        >Stages</button>
                        <button
                            className={`ml-1 ${this.activeFieldTabButtonCss(this.FIELD_TYPES)}`}
                            type="button"
                            onClick={() => this.handleSetActiveFieldTab(this.FIELD_TYPES)}
                        >Types</button>
                        <button
                            className={`ml-1 ${this.activeFieldTabButtonCss(this.FIELD_STATUS)}`}
                            type="button"
                            onClick={() => this.handleSetActiveFieldTab(this.FIELD_STATUS)}
                        >Status</button>
                    </div>
                    <div className="col-12 col-lg-5 text-right mt-3 mt-lg-0">
                        <button
                            className="btn btn-primary"
                            type="button"
                            onClick={() => this.saveSchemeOnClick()}
                        >Save {this.state.activeFieldTab === 'fin_stages' ? 'status' : this.state.activeFieldTab} Filter Scheme</button>
                    </div>
                </div>

                <div className={this.state.isFormValid ? 'd-none' : 'row mt-3'}>
                    <div className="col-12">
                        <ErrorLabel>Percentages need to add up to 100%</ErrorLabel>
                    </div>
                </div>

                <ul className="list-group my-4">
                    { this.displayedFields().map((field) => {
                        return (
                            <li key={`${this.state.activeFieldTab}-${field.value}`} className="list-group-item">
                                <div className="row">
                                    {
                                        this.state.activeFieldTab === this.FIELD_STAGES ?
                                            <>
                                                <div className="col-12 col-lg-5 my-1">{field.value}</div>
                                                <div className="col-6 col-sm-3 col-lg-2 my-1">
                                                    <label className="checkbox">
                                                        <span>Include</span>
                                                        <input type="checkbox" checked={field.visible} onChange={() => this.checkboxOnChange(field, 'visible')} />
                                                    </label>
                                                </div>
                                                <div className="col-6 col-sm-5 col-lg-3 text-right my-1">
                                                    <label className="checkbox">
                                                        <span>Commissions</span>
                                                        <input type="checkbox" checked={field.comm_trigger} onChange={() => this.checkboxOnChange(field, 'comm_trigger')} />
                                                    </label>
                                                </div>
                                                <div className="col-12 col-sm-4 col-lg-2">
                                                    <input className={field.comm_trigger ? 'input input-plain' : 'd-none'}
                                                           placeholder="%"
                                                           type="number"
                                                           value={field.comm_percentage}
                                                           onChange={(event) => this.inputOnChange(field, 'comm_percentage', event.target.value)}
                                                    />
                                                </div>
                                            </>
                                        :
                                            <>
                                                <div className="col-6 col-sm-9 my-1">{field.value}</div>
                                                <div className="col-6 col-sm-3 my-1">
                                                    <label className="checkbox">
                                                        <span>Include</span>
                                                        <input type="checkbox" checked={field.visible} onChange={() => this.checkboxOnChange(field, 'visible')} />
                                                    </label>
                                                </div>
                                            </>
                                    }
                                </div>
                            </li>
                        );
                    })}
                </ul>

                <div className="text-right">
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => this.saveSchemeOnClick()}
                    >Save {this.state.activeFieldTab === 'fin_stages' ? 'status' : this.state.activeFieldTab} Filter Scheme</button>
                </div>
            </div>
        );
    }
};

export default DataFilters;
